// opd-state.js — the OPD encounter state machine as a SHARED, PURE, dependency-
// free library. Under the online-primary redesign (server/opd/state-machine.mjs
// was DELETED in the stage-3 severing) this file is the SINGLE, CANONICAL source
// of truth for the legal-transition table and the opd/state extension. Keeping it
// pure JS (no imports, no I/O, no Medplum, no Node built-ins beyond the globally
// available Web Crypto) means the browser SPA and a Medplum Bot lambda can both
// import it and agree, byte-for-byte, on what a transition means.
//
// LIVE-QUEUE ADDITION (2026-07-12, docs/opd/LIVE_QUEUE_ENGINE.md §5): CALLED sits
// between READY and IN_CONSULT — a token has been announced to a room (the public
// board shows "CAR-007 → Room 5") but the consult has not yet started. READY may
// still go DIRECTLY to IN_CONSULT (the existing call-next / start-consult flow is
// unchanged); the CALLED step is an additive announce state, and a called patient
// can return to READY (re-queue / re-announce) or lapse to EXPIRED at end of day.
//
// PARITY: the exhaustive 10×10 vitest fixture (opd-state.test.js) is an
// INDEPENDENT hand-kept mirror of the tables below — edit both together or the
// build fails (the whole point of replicating the table rather than trusting it).
//   • The state is read canonical-URL-first, legacy-endsWith fallback — the
//     same dual-read the server's readEncounterState() does (lines 48-54), so a
//     Medplum-seeded row (canonical url) AND a main-1 mirrored row (legacy url)
//     both resolve. WRITES stamp the CANONICAL url only, because the online-
//     primary client shapers (queue-row.js / encounter-view.js) read that url
//     and see a legacy-url-only row as stateless → DRAFT (medplum-callin.js
//     header note, lines 26-29).

// The online-primary canonical opd/state extension URL — the one the client
// shapers read and the one WRITES stamp.
export const OPD_STATE_EXTENSION_URL = 'https://healther.io/opd/state';

// The main-1 legacy URL the server's state-machine.mjs stamps. Kept so a
// mirrored/legacy row still resolves its state on READ (never written here).
export const OPD_STATE_EXTENSION_URL_LEGACY =
  'http://healther.io/fhir/StructureDefinition/opd-state';

// Activity coding system for a transition Provenance — verbatim from the
// server's writeProvenance (state-machine.mjs:336).
export const OPD_ACTIVITY_SYSTEM = 'http://healther.io/CodeSystem/opd-activity';

export const STATES = Object.freeze({
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  SUBMITTED: 'SUBMITTED',
  READY: 'READY',
  CALLED: 'CALLED',
  IN_CONSULT: 'IN_CONSULT',
  PRESCRIBED: 'PRESCRIBED',
  COMPLETED: 'COMPLETED',
  ABANDONED: 'ABANDONED',
  EXPIRED: 'EXPIRED',
});

// Maps OPD fine-grained state → FHIR Encounter.status. Verbatim from the server
// (state-machine.mjs:69-79). FHIR Encounter.status has only a handful of legal
// values, so several OPD states collapse onto 'planned'.
export const FHIR_STATUS = Object.freeze({
  DRAFT: 'planned',
  IN_PROGRESS: 'planned',
  SUBMITTED: 'planned',
  READY: 'planned',
  CALLED: 'planned', // summoned to a room but consult not started — still pre-consult
  IN_CONSULT: 'in-progress',
  PRESCRIBED: 'finished',
  COMPLETED: 'finished',
  ABANDONED: 'cancelled',
  EXPIRED: 'cancelled',
});

// Allowed transitions table — every target reachable from each source. VERBATIM
// from server/opd/state-machine.mjs:82-92. The inline reasons are the server's.
export const ALLOWED = Object.freeze({
  DRAFT: Object.freeze(['IN_PROGRESS', 'ABANDONED', 'EXPIRED']),
  IN_PROGRESS: Object.freeze(['SUBMITTED', 'ABANDONED', 'EXPIRED']),
  SUBMITTED: Object.freeze(['READY', 'IN_PROGRESS']), // patient reopens before doctor
  READY: Object.freeze(['CALLED', 'IN_CONSULT', 'IN_PROGRESS', 'EXPIRED']), // announce / direct-open / patient reopen / end-of-day no-show
  CALLED: Object.freeze(['IN_CONSULT', 'READY', 'EXPIRED']), // consult starts / re-queue (no-show, re-announce) / end-of-day lapse
  IN_CONSULT: Object.freeze(['PRESCRIBED', 'READY']), // accidental open can revert pre-capture
  PRESCRIBED: Object.freeze(['COMPLETED']),
  COMPLETED: Object.freeze([]),
  ABANDONED: Object.freeze([]),
  EXPIRED: Object.freeze(['IN_CONSULT']), // walk-in hold timed out OR end-of-day no-show; door back to consult
});

// A uuid that works in the browser (window.crypto) and under Node/Lambda
// (globalThis.crypto — Node ≥ 20 exposes Web Crypto globally). Dependency-free.
function uuid() {
  const c = (typeof globalThis !== 'undefined' && globalThis.crypto) || null;
  if (c && c.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Read the OPD view-state string off an encounter, CANONICAL url first, then the
 * legacy `…/opd-state` endsWith fallback — mirrors the server's readEncounterState
 * (state-machine.mjs:48-54). Returns null when neither extension is present
 * (a stateless row the shapers treat as DRAFT).
 */
export function currentState(enc) {
  if (!enc) return null;
  const exts = (enc && enc.extension) || [];
  const canonical = exts.find((e) => e && e.url === OPD_STATE_EXTENSION_URL);
  if (canonical && canonical.valueString) return canonical.valueString;
  const legacy = exts.find((e) => e && typeof e.url === 'string' && e.url.endsWith('opd-state'));
  return (legacy && legacy.valueString) || null;
}

/** True iff `to` is a legal target from `from` per the ALLOWED table. A same-
 *  state (from === to) is NOT in the table for any state, so this returns false
 *  for it — callers treat that as an idempotent no-op BEFORE calling (the server
 *  does the same: transition() short-circuits from === to before isAllowed). */
export function canTransition(from, to) {
  if (!from || !to) return false;
  return (ALLOWED[from] || []).includes(to);
}

/** The FHIR Encounter.status for an OPD state, or 'planned' when unknown
 *  (matches the server's `FHIR_STATUS[state] || 'planned'` fallback). */
export function fhirStatusFor(state) {
  return FHIR_STATUS[state] || 'planned';
}

/**
 * Return a NEW copy of `enc` with the CANONICAL opd/state extension set to
 * `state` and Encounter.status derived from it. Never mutates the input. If a
 * legacy-url opd-state extension is also present it is kept CONSISTENT (its
 * valueString advanced too) so no reader — canonical or legacy — sees a stale
 * value. Pure; used by buildTransitionUpdate and available on its own for the
 * initial-value-on-create case (setting IN_CONSULT on a fresh walk-in, which is
 * a create not a transition and is legal — see medplum-callin.js).
 */
export function withState(enc, state) {
  const src = Array.isArray(enc && enc.extension) ? enc.extension : [];
  let sawCanonical = false;
  const exts = src.map((e) => {
    if (!e) return e;
    if (e.url === OPD_STATE_EXTENSION_URL) { sawCanonical = true; return { ...e, valueString: state }; }
    if (typeof e.url === 'string' && e.url.endsWith('opd-state')) return { ...e, valueString: state };
    return e;
  });
  if (!sawCanonical) exts.push({ url: OPD_STATE_EXTENSION_URL, valueString: state });
  return { ...enc, status: fhirStatusFor(state), extension: exts };
}

/**
 * Build the two resources a state transition writes IN ONE TRANSACTION: the
 * updated Encounter and its Provenance. PURE — no I/O; the caller (a client lib
 * or a Bot) performs the actual writes (e.g. an updateResource with If-Match +
 * a createResource, or a FHIR transaction Bundle).
 *
 * Mirrors the server's transition() + writeProvenance() (state-machine.mjs:291-346):
 *   • from === to → idempotent no-op: { resource: enc (unchanged), provenance:
 *     null, transitioned: false }. No Provenance is minted (server writes none).
 *   • an illegal edge → throws Error{ code:'BAD_TRANSITION', from, to } — the
 *     SAME error shape the server throws (and the /consult/open route maps to 400).
 *   • a legal edge → { resource: updatedEncounter, provenance, transitioned:true }.
 *
 * @param {object} enc  the current Encounter resource
 * @param {string} to   the target OPD state
 * @param {object} [opts]
 * @param {string} [opts.actor='System']   Provenance agent display
 * @param {string} [opts.activity]         activity code (default `STATE_${to}`)
 * @param {string} [opts.reason]           optional Provenance.reason text
 * @param {object|object[]} [opts.entity]  optional Provenance.entity `what`(s)
 * @param {string} [opts.encounterRef]     Provenance target ref (default
 *                                         `Encounter/${enc.id}`) — pass a
 *                                         urn:local:… when the encounter is itself
 *                                         a not-yet-created (offline) resource.
 */
export function buildTransitionUpdate(enc, to, opts = {}) {
  if (!enc) throw Object.assign(new Error('buildTransitionUpdate: encounter is required'), { code: 'NOT_FOUND' });
  const from = currentState(enc);
  if (from === to) return { resource: enc, provenance: null, transitioned: false };
  if (!canTransition(from, to)) {
    throw Object.assign(new Error(`Disallowed transition ${from} → ${to}`), {
      code: 'BAD_TRANSITION', from, to,
    });
  }
  const resource = withState(enc, to);
  const encounterRef = opts.encounterRef || `Encounter/${enc.id}`;
  const activity = opts.activity || `STATE_${to}`;
  const entity = opts.entity;
  const provenance = {
    resourceType: 'Provenance',
    id: `prov-${uuid()}`,
    target: [{ reference: encounterRef }],
    recorded: new Date().toISOString(),
    activity: { coding: [{ system: OPD_ACTIVITY_SYSTEM, code: activity, display: activity }] },
    agent: [{ type: { coding: [{ code: 'author' }] }, who: { display: opts.actor || 'System' } }],
    ...(opts.reason ? { reason: [{ text: opts.reason }] } : {}),
    ...(entity ? {
      entity: (Array.isArray(entity) ? entity : [entity]).map((e) => ({ role: 'source', what: e })),
    } : {}),
    // The from→to pair the server carries on _healther for its transition audit.
    // Encoded FHIR-natively via extension by the writer (see medplum-consult-extras).
    _healther: { fromState: from, toState: to },
  };
  return { resource, provenance, transitioned: true };
}
